import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  of,
  share,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiLoaderModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { AttachmentService } from '@shared/services/attachment.service';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FilesInputComponent } from '@shared/components/forms/files-input/files-input.component';
import { mimeTypes } from '@shared/constants/mime-types';
import { roundDecimal } from '@shared/utils/math';
import {
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { FormsService } from '@shared/services/forms.service';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { TuiLetModule } from '@taiga-ui/cdk';
import { CURRENCIES } from '@shared/constants/currency';
import { openFileInNewTab } from '@shared/utils/download-file';
import { WINDOW } from '@shared/providers/window.provider';
import { Attachment } from '../../../../../../types/attachment';
import { PreOrder, PreOrderForm } from '../../../../../../types/pre-order';
import { PreOrderService } from '../../../../services/pre-order.service';

export type PreOrderAttachmentsInputData = {
  preOrder: PreOrder;
  canUpload: boolean;
  canRemove: boolean;
  canEdit: boolean;
};

type PreOrderAttachmentsForm = Pick<
  PreOrderForm,
  | 'buyerCode'
  | 'airWaybillNumber'
  | 'imported'
  | 'prepayment'
  | 'prepaymentSum'
  | 'prepaymentCurrencyLetter'
  | 'paymentDate'
  | 'farmBrokerJson'
  | 'brokerJson'
  | 'expectedLoadingDate'
>;

@Component({
  selector: 'app-pre-order-attachments',
  standalone: true,
  imports: [
    AsyncPipe,
    FilesInputComponent,
    TuiButtonModule,
    ReactiveFormsModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    AutocompleteComponent,
    TuiToggleModule,
    InputDateComponent,
    TuiSvgModule,
    TuiLoaderModule,
    TuiLetModule,
  ],
  templateUrl: './pre-order-attachments.component.html',
  styleUrl: './pre-order-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreOrderAttachmentsComponent implements OnInit, OnDestroy {
  refresh$ = new Subject<void>();
  attachmentId$ = new BehaviorSubject<Nullable<number>>(null);
  attachments$ = new BehaviorSubject<Attachment[]>([]);
  openFileInNewTab$ = new Subject<string>();
  attach$ = new Subject<void>();
  saveChanges$ = new Subject<{ close: boolean }>();
  download$ = new Subject<void>();
  delete$ = new Subject<void>();
  files = this.fb.control<Blob[]>([]);
  protected readonly mimeTypes = mimeTypes;
  protected readonly roundDecimal = roundDecimal;
  sellers$ = this.catalogs.sellers$;

  preOrderForm = this.fb.group<PreOrderAttachmentsForm>({
    farmBrokerJson: this.forms.getFarmJsonForm(),
    paymentDate: this.fb.control({ value: null, disabled: true }),
    prepaymentCurrencyLetter: this.fb.control({ value: null, disabled: true }),
    prepaymentSum: this.fb.control({ value: null, disabled: true }),
    prepayment: this.fb.control({ value: null, disabled: true }),
    buyerCode: this.fb.control({ value: null, disabled: true }),
    airWaybillNumber: this.fb.control({ value: null, disabled: true }),
    imported: this.fb.control({ value: null, disabled: true }),
    brokerJson: this.forms.getSellerJsonForm(),
    expectedLoadingDate: this.fb.control({ value: null, disabled: true }),
  });

  protected readonly CURRENCIES = CURRENCIES;
  uploading$ = new BehaviorSubject(false);
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<
      PreOrder,
      PreOrderAttachmentsInputData
    >,
    @Inject(WINDOW) private readonly window: Window,
    private readonly attachmentService: AttachmentService,
    private readonly destroyRef: DestroyRef,
    private readonly fb: FormBuilder,
    private readonly alert: AlertService,
    protected readonly catalogs: CargoCatalogsService,
    private readonly forms: FormsService,
    private readonly preOrderService: PreOrderService,
  ) {}

  onOpenFileInNewTab() {
    return this.openFileInNewTab$
      .pipe(
        withLatestFrom(this.attachmentId$),
        switchMap(([fileName, id]) =>
          this.attachmentService
            .getSingleFile({
              id: id ?? 0,
              cargoId: null,
              fileName,
            })
            .pipe(
              tap(res => {
                if (res.body) {
                  openFileInNewTab(res, this.window);
                }
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onDownload() {
    this.download$
      .pipe(
        map(() => this.attachmentId$.value),
        filter((attachmentId): attachmentId is number => !!attachmentId),
        switchMap(attachmentId =>
          this.attachmentService.download(null, attachmentId).pipe(
            catchError(() => {
              this.alert.showError();
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onRefresh() {
    this.refresh$
      .pipe(
        switchMap(() => {
          if (this.attachmentId$.value) {
            return this.attachmentService
              .getFileListFromAttachment(null, this.attachmentId$.value)
              .pipe(
                catchError(() => {
                  this.alert.showError('Не удалось загрузить файлы');
                  return of([]);
                }),
              );
          }
          return of([]);
        }),
        tap(attachments => {
          this.attachments$.next(attachments);
        }),
      )
      .subscribe();
  }

  onDelete() {
    this.delete$
      .pipe(
        map(() => this.attachmentId$.value),
        filter((attachmentId): attachmentId is number => !!attachmentId),
        switchMap(attachmentId =>
          this.attachmentService.delete(null, attachmentId).pipe(
            tap(() => {
              this.attachmentId$.next(null);
              this.attachments$.next([]);
              this.savePreOrder();
            }),
            catchError(() => {
              this.alert.showError();
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSaveChanges() {
    this.saveChanges$
      .pipe(
        switchMap(config => this.attachFiles().pipe(map(() => config))),
        tap(config => {
          this.savePreOrder();
          if (config.close) {
            this.context.$implicit.complete();
          }
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  get preOrderId() {
    return this.context.data.preOrder.id ?? 0;
  }

  get canEdit() {
    return this.context.data.canEdit;
  }

  get canRemove() {
    return this.context.data.canRemove;
  }

  get canUpload() {
    return this.context.data.canUpload;
  }

  get isImported() {
    return this.preOrderForm.controls.imported.getRawValue();
  }

  getFinalPreOrder() {
    return {
      ...this.context.data.preOrder,
      attachmentId: this.attachmentId$.value,
    };
  }

  savePreOrder() {
    const order: PreOrder = {
      ...this.context.data.preOrder,
      attachmentId: this.attachmentId$.value,
    };
    this.context.$implicit.next(order);
  }

  attachFiles() {
    const files = this.files.getRawValue() ?? [];
    if (!files.length) {
      return of(null);
    }
    const formData = new FormData();
    files.forEach((file, index) => {
      const fileKey = `file${index + 1}`;
      formData.append(fileKey, file as unknown as Blob);
    });

    return this.attachmentService
      .attach(null, this.attachmentId$.value, formData)
      .pipe(
        tap(id => {
          this.files.reset();
          this.attachmentId$.next(id);
          this.refresh$.next();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      );
  }

  setInitialData() {
    const data = this.context.data;
    this.attachmentId$.next(data.preOrder.attachmentId);
    this.preOrderForm.patchValue(data.preOrder);
    this.preOrderForm.controls.brokerJson.disable();
    this.preOrderForm.controls.farmBrokerJson.disable();
  }

  saveChangesWhenDialogClose() {
    if (!this.uploading$.getValue()) {
      const stream = this.attachFiles().pipe(
        switchMap(id => {
          if (id !== null || this.preOrderForm.dirty) {
            return this.preOrderService.update(this.getFinalPreOrder()).pipe(
              tap(() => {
                this.alert.showSuccess();
              }),
            );
          }
          return of(null);
        }),
        catchError(err => {
          this.alert.showError(err.error);
          return EMPTY;
        }),
        share(),
      );
      firstValueFrom(stream);
    }
  }

  ngOnDestroy() {
    this.saveChangesWhenDialogClose();
  }

  ngOnInit() {
    this.setInitialData();
    this.onSaveChanges();
    this.onRefresh();
    this.onDownload();
    this.onDelete();
    this.onOpenFileInNewTab();
    this.refresh$.next();

    if (!this.canEdit) {
      this.files.disable();
    }
  }
}
